import { useState, type ReactNode } from 'react';
import { Columns, type Column } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { VolumeMonth } from '../types';
import { planName } from './account-words';
import { monthOf, type LinkFocus } from './link';
import { RangeSwitch } from './RangeSwitch';
import { SeeTheNumbers } from './SeeTheNumbers';
import type { SortColumn, SortRow } from './SortableTable';
import { usePicked } from './picking';
import { monthSpan, type RenderBreakdown, type Span } from './spans';
import { UsageCard } from './UsageCard';
import { compact, dayWord, monthWord, peakIndex } from './volume-math';
import './insights.css';

type Metric = 'pages' | 'credits';
const METRICS = [
  { value: 'pages' as const, label: 'Pages' },
  { value: 'credits' as const, label: 'Allowance used' },
];

/**
 * One billing period's use (CustomerVolume.periods): `end` is the day before the next start, the last
 * period is the current one so far. Declared here until the volume type carries the field.
 */
export interface RampPeriod {
  start: string;
  end: string;
  credits: number;
  pages: number;
}

/**
 * The months from the first with pages, so the ramp never opens on an empty slot (round 2, dataviz
 * S7: a ramp padded back to six months opened on an empty May before June's first pages). A new
 * account's few months stay columns, not slabs: the chart caps a column's width.
 */
export function fromFirstPages(months: readonly VolumeMonth[]): VolumeMonth[] {
  const first = months.findIndex((m) => m.pages > 0);
  return first < 0 ? [] : months.slice(first);
}

/** The periods from the first with use, for the same reason. */
export function fromFirstUse(periods: readonly RampPeriod[]): RampPeriod[] {
  const first = periods.findIndex((p) => p.pages > 0 || p.credits > 0);
  return first < 0 ? [] : periods.slice(first);
}

/** "Sep 15 – Oct 14": a billing period as the ledger names it. */
export function periodWord(p: RampPeriod, locale: string): string {
  return `${dayWord(p.start, locale)} – ${dayWord(p.end, locale)}`;
}

/** What the card draws for one measure: the columns, what a pick means, and the table behind them. */
interface RampView {
  title: string;
  label: string;
  note: string;
  columns: Column[];
  spanOf: (from: number, to: number) => Span;
  dim: (focus: LinkFocus) => ((i: number) => boolean) | undefined;
  focusOf: (i: number) => LinkFocus;
  numbers: { caption: string; columns: readonly SortColumn[]; rows: SortRow[] };
}

interface RampContext {
  f: Formatter;
  locale: string;
  today: string;
  plan: MonthlyRampCardProps['plan'];
}

function monthColumns(months: readonly VolumeMonth[], metric: Metric, x: RampContext): Column[] {
  const { f, locale } = x;
  const value = (m: VolumeMonth) => (metric === 'pages' ? m.pages : Math.round(m.credits));
  const peak = peakIndex(months.map(value));
  const last = months.length - 1;
  return months.map((m, i) => ({
    axis: monthWord(m.month, locale, i === 0 || m.month.endsWith('-01')),
    value: value(m),
    partial: i === last,
    mark: i === peak || (i === last && value(m) > 0) ? f.int(value(m)) : undefined,
    tip: {
      value:
        metric === 'pages' ? `${f.int(m.pages)} pages` : `${f.int(Math.round(m.credits))} pages`,
      title: `${monthWord(m.month, locale, true)}${i === last ? ', so far' : ''}`,
      lines: [
        metric === 'pages'
          ? `${f.int(Math.round(m.credits))} pages used`
          : `${f.int(m.pages)} pages`,
        `${f.int(m.documents)} documents`,
      ],
      hint: 'Click, or drag across, for the jobs and intake behind it',
    },
  }));
}

/** The span a picked run of months stands for, the month so far ending today. */
function monthsSpan(
  months: readonly VolumeMonth[],
  from: number,
  to: number,
  x: RampContext,
): Span {
  const a = monthSpan(months[from]!.month, x.today, x.locale);
  const b = monthSpan(months[to]!.month, x.today, x.locale);
  return {
    from: a.from,
    to: b.to,
    title: from === to ? a.title : `${monthWord(months[from]!.month, x.locale)} to ${b.title}`,
  };
}

const MONTH_COLUMNS = [
  { heading: 'Month' },
  { heading: 'Pages', numeric: true },
  { heading: 'Documents', numeric: true },
  { heading: 'Pages used', numeric: true },
];

function monthView(months: readonly VolumeMonth[], metric: Metric, x: RampContext): RampView {
  const pages = metric === 'pages';
  return {
    title: pages ? 'Pages a month' : 'Pages used a month',
    label: `${pages ? 'Pages read' : 'Pages used'} a month since the first pages.`,
    note: 'The month so far is hatched.',
    columns: monthColumns(months, metric, x),
    spanOf: (from, to) => monthsSpan(months, from, to, x),
    dim: (focus) => {
      const month = monthOf(focus);
      return month ? (i) => months[i]!.month !== month : undefined;
    },
    focusOf: (i) => ({ kind: 'month', month: months[i]!.month }),
    numbers: {
      caption: 'Pages, documents and pages used a month',
      columns: MONTH_COLUMNS,
      rows: [...months].reverse().map((m) => ({
        key: m.month,
        cells: [
          monthWord(m.month, x.locale, true),
          f0(x.f, m.pages),
          f0(x.f, m.documents),
          f0(x.f, m.credits),
        ],
        values: [m.month, m.pages, m.documents, m.credits],
      })),
    },
  };
}

const f0 = (f: Formatter, n: number) => f.int(Math.round(n));

function periodColumns(periods: readonly RampPeriod[], x: RampContext): Column[] {
  const { f, locale, plan } = x;
  const peak = peakIndex(periods.map((p) => Math.round(p.credits)));
  const last = periods.length - 1;
  return periods.map((p, i) => {
    const value = Math.round(p.credits);
    const now = i === last;
    const ahead = now && plan.projected !== undefined && plan.projected > value;
    return {
      axis: dayWord(p.start, locale),
      value,
      partial: now,
      projected: ahead ? plan.projected : undefined,
      mark: i === peak || (now && value > 0) ? f.int(value) : undefined,
      tip: {
        value: `${f.int(value)} pages`,
        title: `${periodWord(p, locale)}${now ? ', so far' : ''}`,
        lines: [
          ...(ahead
            ? [`On pace for ${f.int(Math.round(plan.projected!))} by ${dayWord(p.end, locale)}`]
            : []),
          `${f.int(p.pages)} pages`,
          ...(plan.credits > 0 ? [`${Math.round((value / plan.credits) * 100)}% of the plan`] : []),
        ],
        hint: 'Click, or drag across, for the jobs and intake behind it',
      },
    };
  });
}

const PERIOD_COLUMNS = [
  { heading: 'Billing period' },
  { heading: 'Pages used', numeric: true },
  { heading: 'Pages', numeric: true },
];

/** The credits view by billing period (round 3, dataviz M1): the plan, the refill and the ledger run on it. */
function periodView(periods: readonly RampPeriod[], x: RampContext): RampView {
  const endOf = (p: RampPeriod) => (p.end < x.today ? p.end : x.today);
  const inside = (p: RampPeriod, day: string) => day >= p.start && day <= p.end;
  const columns = periodColumns(periods, x);
  const paced = columns.at(-1)?.projected !== undefined;
  return {
    title: 'Pages used a billing period',
    label: 'Pages used a billing period since the first pages.',
    note: `The period so far is hatched${paced ? '; the outline above it is the pace to the refill' : ''}.`,
    columns,
    spanOf: (from, to) => ({
      from: periods[from]!.start,
      to: endOf(periods[to]!),
      title:
        from === to
          ? periodWord(periods[from]!, x.locale)
          : `${dayWord(periods[from]!.start, x.locale)} to ${dayWord(endOf(periods[to]!), x.locale, true)}`,
    }),
    dim: (focus) => (focus?.kind === 'day' ? (i) => !inside(periods[i]!, focus.day) : undefined),
    focusOf: () => null,
    numbers: {
      caption: 'Pages used and pages a billing period',
      columns: PERIOD_COLUMNS,
      rows: [...periods].reverse().map((p) => ({
        key: p.start,
        cells: [periodWord(p, x.locale), f0(x.f, p.credits), f0(x.f, p.pages)],
        values: [p.start, p.credits, p.pages],
      })),
    },
  };
}

export interface MonthlyRampCardProps {
  months: readonly VolumeMonth[];
  /** Billing periods, oldest first; without them the credits view falls back to calendar months. */
  periods?: readonly RampPeriod[] | undefined;
  today: string;
  locale: string;
  /**
   * The plan's credits a period, drawn as a line on the credits view, and the current period
   * projected to its end at the pace (the account's `credits.projected`).
   */
  plan: { key: string | null; credits: number; projected?: number | undefined };
  focus: LinkFocus;
  onFocus: (focus: LinkFocus) => void;
  renderBreakdown?: RenderBreakdown | undefined;
}

function viewOf(props: MonthlyRampCardProps, metric: Metric, x: RampContext): RampView {
  const periods = props.periods ? fromFirstUse(props.periods) : [];
  if (metric === 'credits' && periods.length > 0) return periodView(periods, x);
  return monthView(fromFirstPages(props.months), metric, x);
}

/**
 * The ramp: pages a calendar month, or credits a billing period with the plan's credits as a line and
 * the period so far hatched under its projected end, so a period over plan shows at a glance and the
 * chart agrees with the pace and the ledger (the same unit; pages and credits never share an axis).
 * Without periods the credits view falls back to months. A click or a drag opens the detail.
 */
export function MonthlyRampCard(props: MonthlyRampCardProps): ReactNode {
  const { locale, plan } = props;
  const f = useFormat();
  const [metric, setMetric] = useState<Metric>(plan.credits > 0 ? 'credits' : 'pages');
  const view = viewOf(props, metric, { f, locale, today: props.today, plan });
  const pick = usePicked(props.renderBreakdown, view.spanOf);
  const withPlan = metric === 'credits' && plan.credits > 0;
  const name = planName(plan.key);
  return (
    <UsageCard
      id="ad-ramp"
      title={view.title}
      empty={view.columns.length === 0 ? 'No pages yet.' : null}
      note={withPlan ? `The dashed line is what ${name} includes. ${view.note}` : view.note}
      controls={
        <RangeSwitch label="Measure" options={METRICS} value={metric} onChange={setMetric} />
      }
    >
      <Columns
        columns={view.columns}
        format={compact}
        selected={pick.picked}
        label={view.label}
        reference={
          withPlan
            ? { value: plan.credits, label: `${name} includes ${f.int(plan.credits)}` }
            : undefined
        }
        dim={view.dim(props.focus)}
        onSelect={pick.onSelect}
        onSpan={pick.onSpan}
        onActive={(i) => props.onFocus(i === null ? null : view.focusOf(i))}
      />
      {pick.detail}
      <SeeTheNumbers {...view.numbers} />
    </UsageCard>
  );
}
